/**
 * Builds the single caption/credit line under an RSS thumbnail.
 * Caption may contain limited HTML (already sanitized at fetch); credit is plain text and escaped when joined.
 */
export type ComposeRssImageCaptionLineParams = {
  displayThumbnail?: boolean;
  displayImageCaption?: boolean;
  displayImageCredit?: boolean;
  thumbnailCaption?: string | null;
  thumbnailCredit?: string | null;
  creditPrefix?: string | null;
};

export const DEFAULT_RSS_IMAGE_CREDIT_PREFIX = 'Photo:';
export const RSS_IMAGE_CREDIT_PREFIX_NONE = 'None';

const stripTags = (value: string): string => value.replace(/<[^>]*>/g, '');

const endsWithPeriod = (htmlOrText: string): boolean => {
  return stripTags(htmlOrText).trim().endsWith('.');
};

const escapeHtml = (text: string): string => {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
};

const buildCreditPhrase = (credit: string, creditPrefix: string | null | undefined): string => {
  const resolvedPrefix = creditPrefix ?? DEFAULT_RSS_IMAGE_CREDIT_PREFIX;

  if (resolvedPrefix === RSS_IMAGE_CREDIT_PREFIX_NONE) {
    return credit;
  }

  const prefix = resolvedPrefix.trim() || DEFAULT_RSS_IMAGE_CREDIT_PREFIX;
  return `${prefix} ${credit}`;
};

export const composeRssImageCaptionLine = ({
  displayThumbnail,
  displayImageCaption,
  displayImageCredit,
  thumbnailCaption,
  thumbnailCredit,
  creditPrefix = DEFAULT_RSS_IMAGE_CREDIT_PREFIX,
}: ComposeRssImageCaptionLineParams): string | null => {
  if (!displayThumbnail) return null;

  const caption = thumbnailCaption?.trim() ?? '';
  const credit = thumbnailCredit?.trim() ?? '';
  const showCaption = Boolean(displayImageCaption && caption);
  const showCredit = Boolean(displayImageCredit && credit);

  if (!showCaption && !showCredit) return null;

  const creditPhrase = showCredit ? buildCreditPhrase(credit, creditPrefix) : null;

  if (!showCaption && creditPhrase) {
    return escapeHtml(creditPhrase);
  }

  if (!creditPhrase) {
    return caption;
  }

  const separator = endsWithPeriod(caption) ? ' ' : '. ';
  return `${caption}${separator}${escapeHtml(creditPhrase)}`;
};
